import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useIsSupport, useWorkspace } from '@treams/treams-hooks';
import { SearchIcon } from '@treams/treams-icons';
import { Button, Drawer, Input, Skeleton } from '@treams/treams-ui';

import {
	ScoreCategory,
	ScoreModuleState,
	type ScoreModuleStatus,
	type TreamsScoreRow,
} from '../../analytics/treams-score/TreamsScore.types';
import { TreamsScoreTable } from '../../analytics/treams-score/TreamsScoreTable/TreamsScoreTable';
import type { ScoreColumnId } from '../../analytics/treams-score/TreamsScoreTable/cells';
import { useScoreColors } from '../../analytics/treams-score/hooks/useTreamsScore';
import ScoreParamsForm from './analytics/ScoreParamsForm';
import type { LaunchWorkerResponse } from './analytics/launchAnalytics.worker';
import LaunchAnalyticsWorker from './analytics/launchAnalytics.worker?worker';
import {
	SCORE_CONFIG_CHANGED_EVENT,
	loadScoreConfigOverrides,
	notifyScoreConfigRecalculated,
} from './analytics/scoreConfigOverrides';

const MODULE_STATUS: ScoreModuleStatus = {
	[ScoreCategory.Engagement]: ScoreModuleState.Enabled,
	[ScoreCategory.Targets]: ScoreModuleState.Enabled,
	[ScoreCategory.Feedback]: ScoreModuleState.Enabled,
	[ScoreCategory.OneOnOne]: ScoreModuleState.Enabled,
	[ScoreCategory.Conversations]: ScoreModuleState.Enabled,
};

export default function LaunchTreamsScore() {
	const { t } = useTranslation();
	const getScoreColors = useScoreColors();
	const isSupport = useIsSupport();
	const workspace = useWorkspace();

	const [rows, setRows] = useState<TreamsScoreRow[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isRunning, setIsRunning] = useState(false);
	const [search, setSearch] = useState('');
	const [scoreParamsOpen, setScoreParamsOpen] = useState(false);

	useEffect(() => {
		if (!isSupport || !workspace.id) return;

		const worker = new LaunchAnalyticsWorker();
		// Only config-change runs should resolve Bijwerken's await — not the initial load.
		let notifyWhenDone = false;

		setIsRunning(true);

		worker.onmessage = (e: MessageEvent<LaunchWorkerResponse>) => {
			const msg = e.data;

			if (msg.type === 'done') {
				setRows(msg.rows);
				setError(null);
				setIsRunning(false);
				if (notifyWhenDone) {
					notifyWhenDone = false;
					notifyScoreConfigRecalculated();
				}
			}

			if (msg.type === 'error') {
				// A failed run yields no rows; without this the page would show the
				// "no snapshots" message and a GraphQL/auth/network failure would
				// read as a successful empty result.
				setError(msg.message);
				setIsRunning(false);
				if (notifyWhenDone) {
					notifyWhenDone = false;
					notifyScoreConfigRecalculated(msg.message);
				}
			}
		};

		const run = (fromConfigChange = false) => {
			if (fromConfigChange) notifyWhenDone = true;
			setError(null);
			setIsRunning(true);
			worker.postMessage({
				type: 'run',
				workspace: workspace.id,
				endDate: new Date(),
				config: loadScoreConfigOverrides() ?? undefined,
			});
		};

		run();

		// The score-params form (sidebar) saves overrides and fires this event;
		// rerun so the table reflects the tweaked parameters.
		const onConfigChanged = () => run(true);
		window.addEventListener(SCORE_CONFIG_CHANGED_EVENT, onConfigChanged);

		return () => {
			window.removeEventListener(SCORE_CONFIG_CHANGED_EVENT, onConfigChanged);
			worker.postMessage({ type: 'abort' });
			worker.terminate();
		};
	}, [isSupport, workspace.id]);

	const columnLabels = useMemo<Record<ScoreColumnId, string>>(
		() => ({
			total: t('analytics.treamsScore.column.total'),
			[ScoreCategory.Engagement]: t('analytics.treamsScore.factor.engagement'),
			[ScoreCategory.Targets]: t('analytics.treamsScore.factor.targets'),
			[ScoreCategory.Feedback]: t('analytics.treamsScore.factor.feedback'),
			[ScoreCategory.OneOnOne]: t('analytics.treamsScore.factor.oneOnOne'),
			[ScoreCategory.Conversations]: t('analytics.treamsScore.factor.conversations'),
		}),
		[t],
	);

	const filteredRows = useMemo(() => {
		const needle = search.trim().toLowerCase();

		if (!needle) return rows;

		return rows.filter((row) => row.name.toLowerCase().includes(needle));
	}, [rows, search]);

	if (!isSupport) return null;

	if (!rows.length && isRunning) {
		return (
			<>
				<Flex mb={3} alignItems="center">
					<Box flexGrow={1}>
						<Skeleton.Box height={34} />
					</Box>
					<Box ml={2} width={160} style={{ flexShrink: 0 }}>
						<Skeleton.Box height={34} />
					</Box>
				</Flex>
				<Skeleton.Repeat count={8}>
					<Skeleton.Box height={48} mb={2} />
				</Skeleton.Repeat>
			</>
		);
	}

	if (error && !rows.length) {
		return (
			<p style={{ color: 'red' }}>
				{t('settings.analytics.error', {
					defaultValue: 'Scores konden niet worden berekend: {{message}}',
					message: error,
				})}
			</p>
		);
	}

	if (!rows.length) {
		return (
			<p>
				{t('settings.analytics.empty', {
					defaultValue: 'Geen organisaties met een analytics-snapshot.',
				})}
			</p>
		);
	}

	return (
		<>
			<Flex mb={2} alignItems="center">
				<Box width={250} style={{ flexShrink: 0 }}>
					<Input
						icon={<SearchIcon />}
						small
						value={search}
						onChange={setSearch}
						placeholder={t('settings.analytics.filterOrganizations', {
							defaultValue: 'Filter organisaties',
						})}
					/>
				</Box>
				<Flex ml={2} flexShrink={0} alignItems="center">
					<Button
						type="secondary"
						small
						title={t('settings.analytics.scoreParams.title', { defaultValue: 'Score parameters' })}
						onClick={() => setScoreParamsOpen(true)}
					/>
					{isRunning && (
						<Flex ml={2} style={{ opacity: 0.7 }}>
							{t('settings.analytics.calculating', { defaultValue: 'Scores berekenen…' })}
						</Flex>
					)}
					{/* Rerun failed while an earlier result is still on screen; say so
					    instead of leaving the stale scores looking current. */}
					{!isRunning && error && (
						<Flex ml={2} style={{ color: 'red' }}>
							{t('settings.analytics.errorStale', {
								defaultValue:
									'Herberekening mislukt: {{message}}. Onderstaande scores zijn van de vorige berekening.',
								message: error,
							})}
						</Flex>
					)}
				</Flex>
			</Flex>
			<Drawer
				open={scoreParamsOpen}
				onClose={() => setScoreParamsOpen(false)}
				ariaLabel={t('settings.analytics.scoreParams.title', { defaultValue: 'Score parameters' })}
				closeLabel={t('common.close')}
			>
				<ScoreParamsForm />
			</Drawer>
			<TreamsScoreTable
				teams={filteredRows}
				columnLabels={columnLabels}
				moduleStatus={MODULE_STATUS}
				getScoreColors={getScoreColors}
				nameColumnHeader={t('settings.analytics.column.organization', {
					defaultValue: 'Organisaties',
				})}
				showManagerColumn={false}
			/>
		</>
	);
}
